"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { BookIcon, FolderIcon } from "@/components/icons";
import { toneBadge } from "@/lib/statusTone";
import { MAX_KNOWLEDGE_FOLDER_NAME_LENGTH, type KnowledgeFolder, type KnowledgePageFolder } from "@/lib/kb/folderTypes";
import { SidebarSectionHeader } from "./SidebarSectionHeader";
import { KnowledgeFolderTree } from "./KnowledgeFolderTree";
import { useKnowledgeOrganization } from "./useKnowledgeOrganization";

/** Pages are organized independently of automation departments. */
export function KnowledgeNav({
  pages, rawSources, folders = [], pageFolders = [], businessId = "", gapCount, onChanged, onCloseDrawer,
}: {
  pages: string[];
  rawSources: string[];
  folders?: KnowledgeFolder[];
  pageFolders?: KnowledgePageFolder[];
  businessId?: string;
  gapCount?: number;
  onChanged?: () => Promise<void>;
  onCloseDrawer: () => void;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const organization = useKnowledgeOrganization({ businessId, folders, pageFolders, pages, rawSources, onChanged });
  const gapsActive = searchParams.get("view") === "gaps";
  const selectedPage = (searchParams.get("page") || "index").replace(/\.md$/i, "");
  const raw = searchParams.get("raw") === "1";
  const isCurrentPage = (name: string, isRaw = false) =>
    !gapsActive && searchParams.get("new") !== "1" && raw === isRaw && selectedPage === name;
  const selectedClassName = "bg-violet-500/10 font-medium text-violet-300";
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-2 pb-4 pt-1">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <SidebarSectionHeader {...organization.dropProps(null)} className="rounded-lg data-[drop-active=true]:bg-violet-500/20" actions={<>
            <button type="button" title="New folder" aria-label="New knowledge folder" disabled={!businessId || organization.pending}
              onClick={() => organization.startCreate(null)}
              className="flex size-6 cursor-pointer items-center justify-center rounded text-zinc-500 transition hover:text-zinc-200 disabled:cursor-default disabled:opacity-40">
              <FolderIcon className="size-3.5" />
            </button>
            <button type="button" title="New page" onClick={() => router.push("/knowledge?new=1")}
              className="flex size-6 cursor-pointer items-center justify-center rounded text-sm leading-none text-zinc-500 transition hover:text-zinc-200">+</button>
        </>}>Pages</SidebarSectionHeader>
        <section aria-label="Knowledge pages and sources" tabIndex={0} className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg focus-visible:outline-2 focus-visible:outline-violet-400 focus-visible:-outline-offset-2">
          {organization.draft && <form className="mb-2 space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2"
            onSubmit={event => { event.preventDefault(); void organization.saveDraft(); }}>
            <label className="block text-xs text-zinc-400">
              {organization.draft.id ? "Rename folder" : organization.draft.parentId ? `New folder in ${folders.find(folder => folder.id === organization.draft?.parentId)?.name ?? "folder"}` : "New folder"}
              <input autoFocus disabled={organization.pending} aria-label="Knowledge folder name" maxLength={MAX_KNOWLEDGE_FOLDER_NAME_LENGTH} value={organization.draft.name}
                onChange={event => organization.setDraft(current => current ? { ...current, name: event.target.value } : null)}
                onKeyDown={event => { if (event.key === "Escape") organization.setDraft(null); }}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 outline-none focus:border-violet-500" />
            </label>
            <div className="flex justify-end gap-2 text-xs">
              <button type="button" disabled={organization.pending} onClick={() => organization.setDraft(null)} className="cursor-pointer rounded px-2 py-1 text-zinc-400 hover:text-zinc-100">Cancel</button>
              <button type="submit" disabled={organization.pending || !organization.draft.name.trim()} className="cursor-pointer rounded bg-violet-600 px-2 py-1 text-white disabled:cursor-default disabled:opacity-40">Save folder</button>
            </div>
          </form>}
          {organization.error && <p role="alert" className="mb-2 px-3 text-xs text-red-300">{organization.error}</p>}
          <KnowledgeFolderTree pages={pages} rawSources={rawSources} folders={folders} pageFolders={pageFolders}
            organization={organization} isCurrentPage={isCurrentPage} onCloseDrawer={onCloseDrawer} />
        </section>
      </div>
      <div className="shrink-0 border-t border-zinc-800 pt-2">
        <Link href="/knowledge?view=gaps" onClick={onCloseDrawer} aria-current={gapsActive ? "page" : undefined}
          className={`flex h-9 min-w-0 items-center gap-1.5 rounded-lg pl-7 pr-3 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-violet-400 focus-visible:outline-offset-2 ${gapsActive ? selectedClassName : "text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"}`}>
          <BookIcon className="size-3.5 shrink-0 text-zinc-500" />
          <span className="min-w-0 flex-1">Knowledge gaps</span>
          {gapCount !== undefined && <span aria-label={`${gapCount} open knowledge gaps`}
            className={`min-w-5 rounded-md border px-1.5 py-0.5 text-center text-xs tabular-nums ${gapCount > 0 ? toneBadge.attention : "border-transparent text-zinc-500"}`}>{gapCount}</span>}
        </Link>
      </div>
    </div>
  );
}
